import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Container,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Divider,
  Avatar,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  Help as HelpIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../consts';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TicketDetail from './TicketDetail';

const getTypeIcon = (type) => {
  switch (type) {
    case 'bug':
      return <BugReportIcon />;
    case 'user_report':
      return <PersonIcon />;
    case 'feedback':
      return <FeedbackIcon />;
    default:
      return <HelpIcon />;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'bug':
      return 'Bug Report';
    case 'user_report':
      return 'User Report';
    case 'feedback':
      return 'Feedback';
    default:
      return 'Other';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'default';
    default:
      return 'default';
  }
};

const TicketsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const setTokenInProcess = useSelector((state) => state.setTokenInProcess);

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    // Wait for token restoration to complete before checking
    if (setTokenInProcess) {
      return;
    }

    // SECURITY: Check authentication - tokens are now in httpOnly cookies
    // Axios interceptor will automatically add token from cookies
    if (!token) {
      // Check if authenticated via Amplify session
      import('../../utils/authUtils').then(({ isAuthenticated }) => {
        isAuthenticated().then((authenticated) => {
          if (!authenticated) {
            navigate('/login');
            return;
          }
          // If authenticated, axios interceptor will handle token
          fetchTickets();
          fetchStats();
        }).catch(() => {
          navigate('/login');
        });
      });
    } else {
      // Token available, proceed
      fetchTickets();
      fetchStats();
    }
  }, [token, statusFilter, typeFilter, priorityFilter, userIdFilter, setTokenInProcess, navigate]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (userIdFilter) params.user_id = userIdFilter;

      // SECURITY: Token is now in httpOnly cookies, axios interceptor handles it automatically
      const response = await axios.get(`${API_URL}/tickets/all`, {
        params,
      });

      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (error.response?.status === 401) {
        dispatch({ type: 'SET_UNAUTHORIZED' });
      } else if (error.response?.status === 403) {
        // Not admin/support, redirect to user tickets
        navigate('/my-tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // SECURITY: Token is now in httpOnly cookies, axios interceptor handles it automatically
      const response = await axios.get(`${API_URL}/tickets/stats`);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
  };

  const handleTicketUpdated = () => {
    setDetailDialogOpen(false);
    setSelectedTicket(null);
    // SECURITY: Token is now in httpOnly cookies, axios interceptor handles it automatically
    fetchTickets();
    fetchStats();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setStatusFilter('');
    } else if (newValue === 1) {
      setStatusFilter('open');
    } else if (newValue === 2) {
      setStatusFilter('in_progress');
    } else if (newValue === 3) {
      setStatusFilter('resolved');
    } else if (newValue === 4) {
      setStatusFilter('closed');
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <DashboardIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={600}>
            Tickets Dashboard
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={600} color="info.main">
                  {stats.open || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open Tickets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={600} color="warning.main">
                  {stats.in_progress || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={600} color="success.main">
                  {stats.resolved || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Resolved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight={600} color="text.secondary">
                  {stats.closed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Closed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="All" />
            <Tab label="Open" />
            <Tab label="In Progress" />
            <Tab label="Resolved" />
            <Tab label="Closed" />
          </Tabs>
        </Paper>

        {/* Filters */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="user_report">User Report</MenuItem>
              <MenuItem value="feedback">Feedback</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="User ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            placeholder="Filter by user ID"
            sx={{ minWidth: 200 }}
          />
        </Box>
      </Box>

      {tickets.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <DashboardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No tickets found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={() => handleTicketClick(ticket)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      >
                        {getTypeIcon(ticket.type)}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight={600}>
                          {ticket.subject}
                        </Typography>
                        <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                          <Chip
                            label={getTypeLabel(ticket.type)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={ticket.status.replace('_', ' ')}
                            size="small"
                            color={getStatusColor(ticket.status)}
                          />
                          {ticket.priority && (
                            <Chip
                              label={ticket.priority}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                          <Chip
                            label={`User: ${ticket.user_id?.substring(0, 8)}...`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(ticket.update_time).toLocaleDateString()}
                    </Typography>
                  </Box>
                  {ticket.last_message && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {ticket.last_message.message}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTicket && (
        <TicketDetail
          open={detailDialogOpen}
          ticketId={selectedTicket.id}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedTicket(null);
          }}
          onUpdate={handleTicketUpdated}
          isUserView={false}
        />
      )}
    </Container>
  );
};

export default TicketsDashboard;

